/**
 * ¿Esta app corre DENTRO del hub OPUS (iframe de apps.opus.com.mx)?
 *
 * La barra OPUS es la MISMA dentro del hub y en el sitio de la app: la pinta la app. Dentro del
 * hub ya no hay barra del hub; la app le pide al hub por postMessage lo que haga falta (abrir
 * otra app sin pestaña nueva, volver a Mis aplicaciones, ir a Mi plan, mostrar/ocultar la barra).
 *
 * Señales (todas exigen estar en un iframe; en su propia pestaña nunca se oculta nada):
 *   - `window.name` = «opus-hub:<app>»: el hub nombra así cada marco (sobrevive al canje SSO
 *     y a las recargas dentro del marco);
 *   - `?embed=hub` en la URL: el hub lo pone en el `return` del canje SSO y en la URL directa;
 *     se recuerda en sessionStorage porque el router lo quita al redirigir.
 */
const CLAVE = 'opus_embebido_hub';

type Ventana = {
  self: unknown;
  top: unknown;
  name: string;
  location: Pick<Location, 'search'>;
  sessionStorage: Pick<Storage, 'getItem' | 'setItem'>;
};

export function detectarEmbebidoEnHub(win: Ventana): boolean {
  let enIframe: boolean;
  try {
    enIframe = win.self !== win.top;
  } catch {
    // Acceder a `top` de otro origen puede lanzar: eso ya es estar en un iframe.
    enIframe = true;
  }
  if (!enIframe) return false;
  if (String(win.name ?? '').startsWith('opus-hub')) return true;
  let marca = false;
  try {
    marca = new URLSearchParams(win.location.search).get('embed') === 'hub';
  } catch {
    /* sin location: sin marca */
  }
  try {
    if (marca) win.sessionStorage.setItem(CLAVE, '1');
    else marca = win.sessionStorage.getItem(CLAVE) === '1';
  } catch {
    /* modo privado / storage bloqueado: vale solo la URL */
  }
  return marca;
}

let cache: boolean | null = null;

/** Se calcula una vez al arrancar (antes de que el router limpie la URL). */
export function embebidoEnHub(): boolean {
  if (cache === null) cache = typeof window === 'undefined' ? false : detectarEmbebidoEnHub(window);
  return cache;
}

/** Solo pruebas. */
export function _resetEmbebidoEnHub(): void {
  cache = null;
}

/** Mensaje de tema que manda el hub a sus marcos. */
export function temaDelMensaje(data: unknown): 'light' | 'dark' | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { tipo?: unknown; tema?: unknown };
  if (d.tipo !== 'opus-hub:tema') return null;
  return d.tema === 'light' || d.tema === 'dark' ? d.tema : null;
}

/** Estado que manda el hub a sus marcos: qué app se está abriendo y si la barra va abierta. */
export function estadoDelMensaje(data: unknown): { abriendo: string | null; barra: boolean } | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as { tipo?: unknown; abriendo?: unknown; barra?: unknown };
  if (d.tipo !== 'opus-hub:estado') return null;
  return {
    abriendo: typeof d.abriendo === 'string' && d.abriendo ? d.abriendo : null,
    barra: d.barra !== false,
  };
}
